import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Chip } from '@mui/material'
import { getInsIdentityResultMessage } from '@/constants/dmp-connect'
import { InsIdentityResultCode, InsOperation } from '@/core/models/ins-audit.model'

type InsAuditResultChipProps = {
  operation: InsOperation
  resultCode?: InsIdentityResultCode
}

export function getResultDisplay(resultCode: InsIdentityResultCode): { displayCode: string; color: 'success' | 'error' | 'info' } {
  switch (resultCode) {
    case InsIdentityResultCode.UNIQUE_IDENTITY_FOUND:
      return { displayCode: '00', color: 'success' }
    case InsIdentityResultCode.NO_IDENTITY_FOUND:
      return { displayCode: '01', color: 'error' }
    case InsIdentityResultCode.MULTIPLE_IDENTITIES_FOUND:
      return { displayCode: '02', color: 'info' }
    default:
      return { displayCode: String(resultCode), color: 'error' }
  }
}

export function InsAuditResultChip({ operation, resultCode }: InsAuditResultChipProps) {
  const lingui = useLingui()
  if (operation === InsOperation.CHECK_INS_IDENTITY) {
    if (resultCode === InsIdentityResultCode.UNIQUE_IDENTITY_FOUND) {
      return <Chip label={<Trans>OK</Trans>} color="success" size="small" />
    }
    if (resultCode === InsIdentityResultCode.NO_IDENTITY_FOUND) {
      return <Chip label={<Trans>KO</Trans>} color="error" size="small" />
    }
    return <Chip label={<Trans>Error</Trans>} color="error" size="small" />
  }

  if (operation === InsOperation.GET_INS_WITHOUT_CARD) {
    const code = typeof resultCode === 'number' ? resultCode : InsIdentityResultCode.NO_IDENTITY_FOUND
    const { displayCode, color } = getResultDisplay(code)
    const message = lingui._(getInsIdentityResultMessage(code))
    return <Chip label={`${displayCode} - ${message}`} color={color} size="small" />
  }

  if (operation === InsOperation.GET_INS_WITH_CARD) {
    if (typeof resultCode === 'number') {
      const { displayCode, color } = getResultDisplay(resultCode)
      const message = lingui._(getInsIdentityResultMessage(resultCode))
      return <Chip label={`${displayCode} - ${message}`} color={color} size="small" />
    }
    const message = lingui._(getInsIdentityResultMessage(InsIdentityResultCode.NO_IDENTITY_FOUND))
    return <Chip label={`01 - ${message}`} color="error" size="small" />
  }

  if (operation === InsOperation.ACCESS_PATIENT_RECORD) {
    return <Chip label={<Trans>Access logged</Trans>} color="success" size="small" variant="outlined" />
  }

  if (operation === InsOperation.UPDATE_PATIENT_IDENTITY) {
    return <Chip label={<Trans>Update logged</Trans>} color="success" size="small" variant="outlined" />
  }

  if (operation === InsOperation.RECEIVE_EXTERNAL_IDENTITY) {
    return <Chip label={<Trans>Incoming flux logged</Trans>} color="info" size="small" variant="outlined" />
  }

  return <Chip label={<Trans>Error</Trans>} color="error" size="small" />
}
